import type { Claim, Payor, Provider } from 'interfaces';
import type { MRT_Row, MRT_TableInstance } from 'mantine-react-table';
import { useEffect, useState, type MutableRefObject } from 'react';
import { useClaimDraftStore } from 'store/ClaimDraftStore';
import { ClaimFormProvider } from './ClaimFormProvider';
import { ClaimEditModal, ClaimForm } from './ModalComponents';
import type { ClaimConfidenceInfo } from '@billing/validation';
import { useLiveClaimRules } from 'hooks/useLiveClaimRules';

const EDIT_RULE_IGNORED_FIELDS = ['internalComments'] as const;

interface ClaimEditRowModalContentProps {
  table: MRT_TableInstance<Claim>;
  row: MRT_Row<Claim>;
  internalEditComponents: React.ReactNode[];
  providers: Provider[];
  payors: Payor[];
  dirtyFields: MutableRefObject<{ [key: string]: boolean }>;
  confidenceMetadata?: ClaimConfidenceInfo | null;
  headerActionsTarget?: string;
  stagedTariffActionsRef?: MutableRefObject<{
    claimID: string | null;
    hasStagedActions: boolean;
  }>;
}

export const ClaimEditRowModalContent = ({
  table,
  row,
  internalEditComponents,
  providers,
  payors,
  dirtyFields,
  confidenceMetadata,
  headerActionsTarget,
  stagedTariffActionsRef,
}: ClaimEditRowModalContentProps) => {
  const [usedDraft, setUsedDraft] = useState<boolean>(false);

  const editDraft = useClaimDraftStore().getEditDraftById(row.original.id);

  const liveRules = useLiveClaimRules({
    claimID: row.original.id,
    ignoredFields: EDIT_RULE_IGNORED_FIELDS,
  });
  const editForm = ClaimForm({
    row,
    onValuesChange: liveRules.scheduleEvaluation,
  });

  useEffect(() => {
    liveRules.scheduleEvaluation(editForm.getValues());
  }, []);

  if (editDraft && !usedDraft) {
    editForm.setValues(editDraft);
    setUsedDraft(true);
  }
  return (
    <ClaimFormProvider
      form={{ formType: 'edit', editForm, createForm: undefined }}
    >
      <ClaimEditModal
        table={table}
        row={row}
        internalEditComponents={internalEditComponents}
        dirtyFields={dirtyFields}
        stagedTariffActionsRef={stagedTariffActionsRef}
        providers={providers}
        payors={payors}
        confidenceMetadata={confidenceMetadata}
        headerActionsTarget={headerActionsTarget}
        liveRules={liveRules}
      />
    </ClaimFormProvider>
  );
};
